import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Store,
  MapPin,
  Phone,
  Mail,
  Globe,
  Clock,
  Star,
  Package,
  Edit,
  Loader2,
  ExternalLink,
  Leaf,
  ArrowLeft,
  DollarSign,
  Wrench
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShopDetail = () => {
  const { shopId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newReview, setNewReview] = useState({ rating: 5, comment: '' });
  const [hoveredRating, setHoveredRating] = useState(0);

  const isOwner = user?.id === shop?.owner_id;

  useEffect(() => {
    fetchShop();
  }, [shopId]);

  const fetchShop = async () => {
    try {
      const [shopRes, productsRes, reviewsRes] = await Promise.all([
        axios.get(`${API}/shops/${shopId}`),
        axios.get(`${API}/shops/${shopId}/products`),
        axios.get(`${API}/shops/${shopId}/reviews`)
      ]);
      setShop(shopRes.data);
      setProducts(productsRes.data);
      setReviews(reviewsRes.data);
    } catch (error) {
      console.error('Error fetching shop:', error);
      toast.error('Shop not found');
      navigate('/marketplace');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/shops/${shopId}/reviews`, newReview);
      setReviews([response.data, ...reviews]);
      
      // Update shop rating
      const newRatingCount = shop.rating_count + 1;
      const newAvgRating = ((shop.average_rating * shop.rating_count) + newReview.rating) / newRatingCount;
      setShop({ ...shop, average_rating: newAvgRating, rating_count: newRatingCount });
      
      setNewReview({ rating: 5, comment: '' });
      setReviewOpen(false);
      toast.success('Review submitted!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (rating, interactive = false, size = 'w-5 h-5') => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            disabled={!interactive}
            onClick={() => interactive && setNewReview({ ...newReview, rating: star })}
            onMouseEnter={() => interactive && setHoveredRating(star)}
            onMouseLeave={() => interactive && setHoveredRating(0)}
            className={interactive ? 'cursor-pointer' : 'cursor-default'}
          >
            <Star
              className={`${size} transition-colors ${
                star <= (interactive ? (hoveredRating || newReview.rating) : rating)
                  ? 'text-warning fill-warning'
                  : 'text-muted-foreground/30'
              }`}
            />
          </button>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!shop) return null;

  return (
    <div className="min-h-screen bg-background">
      {/* Banner */}
      <div className="h-48 md:h-64 bg-gradient-to-br from-primary/20 to-secondary/20 relative">
        {shop.banner_url && (
          <img 
            src={shop.banner_url}
            alt=""
            className="w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent" />
      </div>

      <div className="container mx-auto px-4">
        {/* Shop Header */}
        <div className="relative -mt-20 mb-8">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            {/* Logo */}
            <div className="w-32 h-32 rounded-2xl bg-card border-4 border-card overflow-hidden shadow-lg">
              {shop.logo_url ? (
                <img src={shop.logo_url} alt={shop.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-primary/10 flex items-center justify-center">
                  <Store className="w-16 h-16 text-primary" />
                </div>
              )}
            </div>

            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h1 className="text-3xl font-heading font-medium">{shop.name}</h1>
                    {shop.is_verified && (
                      <Badge className="bg-primary">Verified</Badge>
                    )}
                  </div>
                  <Badge variant="secondary" className="mb-3">{shop.category}</Badge>
                  
                  {/* Rating */}
                  <div className="flex items-center gap-2 mb-4">
                    {renderStars(shop.average_rating)}
                    <span className="text-lg font-medium">{shop.average_rating.toFixed(1)}</span>
                    <span className="text-muted-foreground">({shop.rating_count} reviews)</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  {isOwner ? (
                    <Button variant="outline">
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Shop
                    </Button>
                  ) : (
                    <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
                      <DialogTrigger asChild>
                        <Button className="btn-primary" data-testid="write-review-btn">
                          <Star className="w-4 h-4 mr-2" />
                          Write Review
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle className="font-heading">Review {shop.name}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Rating</label>
                            <div className="flex justify-center py-2">
                              {renderStars(newReview.rating, true, 'w-8 h-8')}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-medium">Your Review (optional)</label>
                            <Textarea
                              placeholder="Share your experience..."
                              value={newReview.comment}
                              onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                              rows={4}
                              data-testid="review-comment-input"
                            />
                          </div>
                          <Button 
                            className="w-full btn-primary"
                            onClick={handleSubmitReview}
                            disabled={submitting}
                            data-testid="submit-review-btn"
                          >
                            {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                            Submit Review
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                </div>
              </div>

              <p className="text-muted-foreground max-w-2xl">{shop.description}</p>
            </div>
          </div>
        </div>

        {/* Shop Info Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="p-4">
              <h3 className="font-medium mb-3">Contact Information</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="w-4 h-4" />
                  <span>{shop.location}</span>
                </div>
                {shop.contact_email && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="w-4 h-4" />
                    <a href={`mailto:${shop.contact_email}`} className="hover:text-primary">
                      {shop.contact_email}
                    </a>
                  </div>
                )}
                {shop.contact_phone && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="w-4 h-4" />
                    <a href={`tel:${shop.contact_phone}`} className="hover:text-primary">
                      {shop.contact_phone}
                    </a>
                  </div>
                )}
                {shop.website && (
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Globe className="w-4 h-4" />
                    <a href={shop.website} target="_blank" rel="noopener noreferrer" className="hover:text-primary flex items-center gap-1">
                      Visit Website <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <h3 className="font-medium mb-3">Business Hours</h3>
              {shop.business_hours ? (
                <div className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Clock className="w-4 h-4 mt-0.5" />
                  <span>{shop.business_hours}</span>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Hours not specified</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <h3 className="font-medium mb-3">Services Offered</h3>
              {shop.services_offered && shop.services_offered.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {shop.services_offered.map((service) => (
                    <Badge key={service} variant="secondary">{service}</Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No services listed</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="products" className="space-y-6">
          <TabsList>
            <TabsTrigger value="products" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Products ({products.length})
            </TabsTrigger>
            <TabsTrigger value="reviews" className="flex items-center gap-2">
              <Star className="w-4 h-4" />
              Reviews ({reviews.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="products">
            {products.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Package className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground">No products listed yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {products.map((item) => (
                  <Card 
                    key={item.id}
                    className="card-interactive overflow-hidden cursor-pointer"
                    onClick={() => navigate(`/marketplace/${item.id}`)}
                  >
                    <div className="aspect-square bg-muted relative overflow-hidden">
                      {item.images && item.images.length > 0 ? (
                        <img 
                          src={item.images[0]}
                          alt={item.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          {item.item_type === 'service' ? (
                            <Wrench className="w-16 h-16 text-muted-foreground/30" />
                          ) : (
                            <Leaf className="w-16 h-16 text-muted-foreground/30" />
                          )}
                        </div>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-medium mb-1 line-clamp-1">{item.title}</h3>
                      {item.rating_count > 0 && (
                        <div className="flex items-center gap-1 mb-2">
                          {renderStars(item.average_rating, false, 'w-3 h-3')}
                          <span className="text-xs text-muted-foreground">({item.rating_count})</span>
                        </div>
                      )}
                      <div className="price-tag flex items-center">
                        <DollarSign className="w-4 h-4" />
                        {item.price.toFixed(2)}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="reviews">
            {reviews.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Star className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                  <p className="text-muted-foreground mb-4">No reviews yet</p>
                  {!isOwner && (
                    <Button className="btn-primary" onClick={() => setReviewOpen(true)}>
                      Be the First to Review
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {reviews.map((review) => (
                  <Card key={review.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <Avatar 
                          className="w-10 h-10 cursor-pointer"
                          onClick={() => navigate(`/profile/${review.reviewer_username}`)}
                        >
                          <AvatarImage src={review.reviewer_avatar} />
                          <AvatarFallback>{review.reviewer_username?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span 
                              className="font-medium cursor-pointer hover:underline"
                              onClick={() => navigate(`/profile/${review.reviewer_username}`)}
                            >
                              @{review.reviewer_username}
                            </span>
                            <span className="text-sm text-muted-foreground">
                              {formatDistanceToNow(new Date(review.created_at), { addSuffix: true })}
                            </span>
                          </div>
                          <div className="mb-2">
                            {renderStars(review.rating, false, 'w-4 h-4')}
                          </div>
                          {review.comment && (
                            <p className="text-muted-foreground">{review.comment}</p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ShopDetail;
